<script lang="ts" setup>
import { computed, ref, watch, onBeforeUnmount } from "vue";
import type { CSSProperties, PropType } from "vue";
import {
  useElementBounding,
  useEventListener,
  usePreferredReducedMotion,
  useThrottleFn,
} from "@vueuse/core";
import { useProjector, AnalyticsService, Events } from "@/util";
import type { LaserPointer } from "@/models";
import { LASER_HIDDEN } from "./PdfJsPresenter.zoom";
import {
  containedVideoBounds,
  pointFromVideo,
} from "./VideoPresenter.laserpointer";

const videoContainer = ref<HTMLDivElement>();
const videoElement = ref<HTMLVideoElement>();
const controlsElement = ref<HTMLDivElement>();
const progressElement = ref<HTMLProgressElement>();
const intrinsicVideoWidth = ref(0);
const intrinsicVideoHeight = ref(0);

const {
  left: videoLeft,
  top: videoTop,
  width: videoWidth,
  height: videoHeight,
} = useElementBounding(videoElement);
const { left: containerLeft, top: containerTop } =
  useElementBounding(videoContainer);

const videoContentBounds = computed(() =>
  containedVideoBounds(
    {
      left: videoLeft.value,
      top: videoTop.value,
      width: videoWidth.value,
      height: videoHeight.value,
    },
    intrinsicVideoWidth.value,
    intrinsicVideoHeight.value,
  ),
);

const laserpointerStyles = computed<CSSProperties>(() => {
  const point = props.laserpointer;
  const bounds = videoContentBounds.value;
  if (
    !point ||
    (point.x === LASER_HIDDEN.x && point.y === LASER_HIDDEN.y) ||
    intrinsicVideoWidth.value <= 0 ||
    intrinsicVideoHeight.value <= 0 ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    return { display: "none" };
  }

  return {
    left: `${bounds.left - containerLeft.value + point.x * bounds.width - 10}px`,
    top: `${bounds.top - containerTop.value + point.y * bounds.height - 10}px`,
  };
});

enum VIDEO_STATE {
  LOADING = "LOADING",
  PLAYING = "PLAYING",
  SEEK = "SEEK",
  PAUSED = "PAUSED",
}

const currentState = ref<VIDEO_STATE>(VIDEO_STATE.LOADING);
const muted = ref<boolean>(false);
const controlsVisible = ref(true);
const prefersReducedMotion = usePreferredReducedMotion();

const CONTROLS_HIDE_DELAY_MS = 3000;
let controlsHideTimer: ReturnType<typeof setTimeout> | null = null;
let controlsHovered = false;
let controlsFocused = false;
let lastInputWasKeyboard = true;
let sourceGeneration = 0;

const clearControlsHideTimer = () => {
  if (controlsHideTimer !== null) {
    clearTimeout(controlsHideTimer);
    controlsHideTimer = null;
  }
};

const scheduleControlsHide = () => {
  clearControlsHideTimer();
  if (
    currentState.value !== VIDEO_STATE.PLAYING ||
    controlsHovered ||
    controlsFocused
  ) {
    return;
  }

  controlsHideTimer = setTimeout(() => {
    controlsHideTimer = null;
    if (
      currentState.value === VIDEO_STATE.PLAYING &&
      !controlsHovered &&
      !controlsFocused
    ) {
      controlsVisible.value = false;
    }
  }, CONTROLS_HIDE_DELAY_MS);
};

const showControlsForActivity = () => {
  controlsVisible.value = true;
  scheduleControlsHide();
};

const handleControlsPointerEnter = () => {
  controlsHovered = true;
  showControlsForActivity();
};

const handleControlsPointerLeave = () => {
  controlsHovered = false;
  scheduleControlsHide();
};

const handleControlsFocusIn = () => {
  controlsFocused = lastInputWasKeyboard;
  showControlsForActivity();
};

const handleControlsFocusOut = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget;
  controlsFocused =
    lastInputWasKeyboard &&
    nextTarget instanceof Node &&
    Boolean(controlsElement.value?.contains(nextTarget));
  scheduleControlsHide();
};

const handleKeyboardActivity = () => {
  lastInputWasKeyboard = true;
  controlsFocused = Boolean(
    controlsElement.value?.contains(document.activeElement),
  );
  showControlsForActivity();
};

const handlePlayerFocusIn = (event: FocusEvent) => {
  if (!lastInputWasKeyboard) {
    return;
  }

  if (
    event.target instanceof Node &&
    controlsElement.value?.contains(event.target)
  ) {
    controlsFocused = true;
  }
  showControlsForActivity();
};

const handlePlayerPointerDown = (event: PointerEvent) => {
  lastInputWasKeyboard = false;
  if (event.pointerType === "touch") {
    showControlsForActivity();
  }
};

useEventListener(
  window,
  "keydown",
  () => {
    lastInputWasKeyboard = true;
  },
  { capture: true },
);
useEventListener(
  window,
  "pointerdown",
  () => {
    lastInputWasKeyboard = false;
  },
  { capture: true },
);

watch(currentState, (state) => {
  controlsVisible.value = true;
  if (state === VIDEO_STATE.PLAYING) {
    scheduleControlsHide();
  } else {
    clearControlsHideTimer();
  }
});

const props = defineProps({
  document: {
    type: String,
    required: true,
  },
  autoplay: {
    type: Boolean,
    required: false,
    default: true,
  },
  documentId: {
    type: String,
    required: true,
  },
  senderContext: {
    type: [String, Number] as PropType<string | number>,
    required: false,
    default: undefined,
  },
  projectorDocumentIdentity: {
    type: String,
    required: false,
    default: undefined,
  },
  laserpointer: {
    type: Object as PropType<LaserPointer>,
    required: false,
    default: undefined,
  },
  userControlled: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits<{
  (e: "rendered"): void;
  (e: "update:laserpointer", value: LaserPointer): void;
}>();

const handlePlayFailure = () => {
  if (videoElement.value?.paused !== false) {
    currentState.value = VIDEO_STATE.PAUSED;
    showControlsForActivity();
  }
};

const play = () => {
  const requestedSourceGeneration = sourceGeneration;
  try {
    const playRequest = videoElement.value!.play();
    void playRequest?.catch(() => {
      if (requestedSourceGeneration === sourceGeneration) {
        handlePlayFailure();
      }
    });
  } catch {
    handlePlayFailure();
  }
};

const pause = () => {
  videoElement.value!.pause();
};

const togglePlayback = () => {
  if (currentState.value === VIDEO_STATE.PLAYING) {
    pause();
  } else {
    play();
  }
};

const ended = () => {
  hideLaser();
  currentState.value = VIDEO_STATE.PAUSED;
  showControlsForActivity();
  AnalyticsService.sendEvent(Events.StopVideo, {
    document_id: props.documentId,
    video_position: currentTime.value,
  });
};

const loadedData = () => {
  currentState.value =
    videoElement.value?.paused !== false
      ? VIDEO_STATE.PAUSED
      : VIDEO_STATE.PLAYING;
  showControlsForActivity();
  emit("rendered");
};

const duration = ref<number>(0);
const setDuration = () => {
  duration.value = videoElement.value!.duration;
  intrinsicVideoWidth.value = videoElement.value!.videoWidth;
  intrinsicVideoHeight.value = videoElement.value!.videoHeight;
};

const currentTime = ref<number>(0);
const setCurrentTime = () => {
  if (!videoElement.value) {
    return 0;
  }

  currentTime.value = videoElement.value!.currentTime;
};

const skipAhead = (ev: MouseEvent) => {
  const rect = progressElement.value!.getBoundingClientRect();
  const pos = (ev.pageX - rect.left) / progressElement.value!.offsetWidth;
  videoElement.value!.currentTime = pos * duration.value;
};

const { videoState } = useProjector();
watch(
  [
    () => currentState.value /*, () => currentTime.value*/,
    () => props.projectorDocumentIdentity,
  ],
  ([newState, documentIdentity]) => {
    videoState.value = {
      state: VIDEO_STATE[newState],
      position: currentTime.value,
      documentIdentity: documentIdentity ?? null,
    };
  },
  { immediate: true }, // This ensures the initial sync happens right away
);

const mute = () => {
  muted.value = true;
};

const unmute = () => {
  muted.value = false;
};

const toggleMuted = () => {
  if (muted.value) {
    unmute();
  } else {
    mute();
  }
};

const LASER_LINGER_MS = 4000;
const LASER_THROTTLE_MS = 50;

let laserActive = false;
let laserGestureEverActive = false;
let laserCursor: { x: number; y: number } | null = null;
let laserLingerTimer: ReturnType<typeof setTimeout> | null = null;

const clearLaserLinger = () => {
  if (laserLingerTimer !== null) {
    clearTimeout(laserLingerTimer);
    laserLingerTimer = null;
  }
};

const emitLaserPointThrottled = useThrottleFn(
  (point: LaserPointer) => {
    if (laserActive) emit("update:laserpointer", point);
  },
  LASER_THROTTLE_MS,
  true,
);

const emitLaserCurrent = () => {
  if (
    !laserActive ||
    !laserCursor ||
    !videoElement.value ||
    intrinsicVideoWidth.value <= 0 ||
    intrinsicVideoHeight.value <= 0
  ) {
    return;
  }

  const point = pointFromVideo(
    containedVideoBounds(
      videoElement.value.getBoundingClientRect(),
      intrinsicVideoWidth.value,
      intrinsicVideoHeight.value,
    ),
    laserCursor.x,
    laserCursor.y,
  );
  if (!point) return;

  laserGestureEverActive = true;
  emitLaserPointThrottled(point);
};

const startLaserLinger = () => {
  clearLaserLinger();
  laserLingerTimer = setTimeout(() => {
    laserLingerTimer = null;
    laserGestureEverActive = false;
    emit("update:laserpointer", { ...LASER_HIDDEN });
  }, LASER_LINGER_MS);
};

const setLaserActive = (active: boolean) => {
  if (active === laserActive) return;
  laserActive = active;
  if (active) {
    clearLaserLinger();
    emitLaserCurrent();
  } else if (laserGestureEverActive && laserLingerTimer === null) {
    startLaserLinger();
  }
};

const hideLaser = () => {
  clearLaserLinger();
  laserActive = false;
  laserCursor = null;
  if (laserGestureEverActive) {
    laserGestureEverActive = false;
    emit("update:laserpointer", { ...LASER_HIDDEN });
  }
};

const laserWindowTarget = computed(() =>
  props.userControlled ? window : null,
);

useEventListener(laserWindowTarget, "mousemove", (event: MouseEvent) => {
  laserCursor = { x: event.clientX, y: event.clientY };
  setLaserActive(event.ctrlKey || event.metaKey);
  if (laserActive) emitLaserCurrent();
});

useEventListener(laserWindowTarget, "keydown", (event: KeyboardEvent) => {
  if (event.key !== "Control" && event.key !== "Meta") return;
  if (laserCursor) setLaserActive(true);
});

useEventListener(laserWindowTarget, "keyup", (event: KeyboardEvent) => {
  if (event.key !== "Control" && event.key !== "Meta") return;
  setLaserActive(event.ctrlKey || event.metaKey);
});

useEventListener(laserWindowTarget, "blur", () => {
  setLaserActive(false);
});

watch(
  () => props.document,
  () => {
    sourceGeneration += 1;
    clearControlsHideTimer();
    controlsFocused = false;
    controlsVisible.value = true;
    currentState.value = VIDEO_STATE.LOADING;
    duration.value = 0;
    currentTime.value = 0;
    intrinsicVideoWidth.value = 0;
    intrinsicVideoHeight.value = 0;
    hideLaser();
  },
);
watch(() => props.senderContext, hideLaser);
watch(
  () => props.userControlled,
  (enabled) => {
    if (!enabled) hideLaser();
  },
);

const trackPlay = () => {
  AnalyticsService.sendEvent(Events.PlayVideo, {
    video_position: currentTime.value,
    document_id: props.documentId,
  });
};

const trackPause = () => {
  AnalyticsService.sendEvent(Events.PauseVideo, {
    video_position: currentTime.value,
    document_id: props.documentId,
  });
};

const handleMediaPlay = () => {
  currentState.value = VIDEO_STATE.PLAYING;
  showControlsForActivity();
  trackPlay();
};

const handleMediaPause = () => {
  currentState.value = VIDEO_STATE.PAUSED;
  showControlsForActivity();
  trackPause();
};

const trackSeek = () => {
  AnalyticsService.sendEvent(Events.SeekVideo, {
    video_position: currentTime.value,
    document_id: props.documentId,
  });
};

onBeforeUnmount(() => {
  clearControlsHideTimer();
  hideLaser();
  AnalyticsService.sendEvent(Events.StopVideo, {
    document_id: props.documentId,
    video_position: currentTime.value,
  });
});
</script>

<template>
  <div>
    <div
      ref="videoContainer"
      class="video_container"
      @pointermove="showControlsForActivity"
      @pointerdown.capture="handlePlayerPointerDown"
      @focusin.capture="handlePlayerFocusIn"
      @keydown.capture="handleKeyboardActivity"
    >
      <div class="video_overlay">
        <span
          v-if="currentState === VIDEO_STATE.PAUSED"
          class="material-symbols-outlined"
          @click="play"
          >play_arrow</span
        >
      </div>
      <video
        @loadeddata="loadedData"
        @loadedmetadata="setDuration"
        @timeupdate="setCurrentTime"
        @ended="ended"
        @pause="handleMediaPause"
        @play="handleMediaPlay"
        @seeked="trackSeek"
        :src="document"
        ref="videoElement"
        :autoplay="autoplay"
        :muted="muted"
        preload="auto"
        playsinline="true"
        webkit-playsinline="webkit-playsinline"
      >
        <source :src="document" type="'video/mp4'" />
      </video>
      <svg
        v-if="laserpointer"
        class="video_laserpointer"
        :style="laserpointerStyles"
        height="20"
        width="20"
      >
        <circle
          cx="10"
          cy="10"
          r="10"
          :fill="laserpointer.color ?? '#db006e'"
        />
      </svg>
      <div
        ref="controlsElement"
        class="controls"
        :class="{
          controls_hidden: !controlsVisible,
          controls_reduced_motion: prefersReducedMotion === 'reduce',
        }"
        @pointerenter="handleControlsPointerEnter"
        @pointerleave="handleControlsPointerLeave"
        @focusin="handleControlsFocusIn"
        @focusout="handleControlsFocusOut"
      >
        <button
          v-if="currentState !== VIDEO_STATE.LOADING"
          @click="togglePlayback"
          class="control_button"
          type="button"
          :aria-label="
            $t(
              currentState === VIDEO_STATE.PLAYING
                ? 'documentPresenter.controls.pause'
                : 'documentPresenter.controls.play',
            )
          "
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            {{ currentState === VIDEO_STATE.PLAYING ? "pause" : "play_arrow" }}
          </span>
        </button>

        <progress
          ref="progressElement"
          class="progress"
          @click="skipAhead"
          :max="duration"
          :min="0"
          :value="currentTime"
        ></progress>

        <button
          @click="toggleMuted"
          class="control_button"
          type="button"
          :aria-label="
            $t(
              muted
                ? 'documentPresenter.controls.unmute'
                : 'documentPresenter.controls.mute',
            )
          "
        >
          <span class="material-symbols-outlined" aria-hidden="true">
            {{ muted ? "volume_off" : "volume_up" }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video_container {
  position: relative;
  height: 100%;
  width: 100%;
}

.video_overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  background: #bbb;
  padding: 0em;
  border-radius: 10%;
}

.video_overlay .material-symbols-outlined {
  font-size: 80px;
  opacity: 0.6;
}

.video_overlay .material-symbols-outlined:hover {
  cursor: pointer;
  opacity: 0.6;
}

video {
  height: 100%;
  width: 100%;
  display: block;
  object-fit: contain;
  object-position: center;
}

.video_laserpointer {
  position: absolute;
  z-index: 96;
  pointer-events: none;
  user-select: none;
}

.controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 95;
  display: flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 52px;
  padding: 4px 6px;
  color: #fff;
  background: rgba(18, 18, 18, 0.68);
  opacity: 1;
  visibility: visible;
  transition: opacity 160ms ease;
}

.controls_hidden {
  opacity: 0;
  pointer-events: none;
}

.controls_reduced_motion {
  transition: none;
}

progress::-webkit-progress-bar {
  background-color: rgba(255, 255, 255, 0.42);
  width: 100%;
}
progress {
  background-color: rgba(255, 255, 255, 0.42);
}

progress::-webkit-progress-value {
  background-color: #db006e !important;
}
progress::-moz-progress-bar {
  background-color: #db006e !important;
}

progress {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  height: 8px;
  margin: 0;
  border: 0;
  color: #db006e;
  cursor: pointer;
}

.control_button {
  display: grid;
  flex: 0 0 44px;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.control_button:hover {
  background: rgba(255, 255, 255, 0.12);
}

.controls .control_button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: -3px;
}

.control_button .material-symbols-outlined {
  width: 32px;
  height: 32px;
  font-size: 32px;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .controls,
  .controls_hidden {
    transition: none;
  }
}
</style>
